import { useState, useMemo, useRef } from "react";
import {
  MarkdownTextPrimitive,
  unstable_memoizeMarkdownComponents as memoizeMarkdownComponents,
} from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import "@assistant-ui/react-markdown/styles/dot.css";
import { LoadingOutlined } from "@ant-design/icons";
import CollapsibleSection from "../CollapsibleSection/CollapsibleSection";
import ChatTextAnimation from "../../VLChat/ChatTextAnimation";
import CodeHeader from "./CodeHeader/CodeHeader";
import {
  parseCollapsibleSectionsWithBuffering,
  type CollapsibleSectionData,
} from "./utils/parseCollapsibleSections";
import styles from "./MarkdownText.module.scss";

// Memoize components to prevent re-renders during streaming
const defaultComponents = memoizeMarkdownComponents({
  CodeHeader,
});

/**
 * MarkdownText renders assistant message content with:
 * - Markdown formatting (GFM)
 * - Code blocks with copy/download buttons
 * - Generic collapsible sections
 */
export default function MarkdownText() {
  // Store extracted collapsible sections and processed content
  const sectionsRef = useRef<CollapsibleSectionData[]>([]);
  const [sections, setSections] = useState<CollapsibleSectionData[]>([]);
  const [hasBufferedContent, setHasBufferedContent] = useState<boolean>(false);

  // Preprocess function that applies smart buffering to incoming text
  const preprocess = useMemo(
    () => (text: string) => {
      // Apply smart buffering to the incoming text
      const {
        visibleContent: processedContent,
        sections: extractedSections,
        hasBufferedContent: isBuffered,
      } = parseCollapsibleSectionsWithBuffering(text);

      // Update sections state
      sectionsRef.current = extractedSections;
      setSections(extractedSections);
      setHasBufferedContent(isBuffered);

      // Simple: just return the processed content, let natural replacement happen
      return processedContent;
    },
    [],
  );

  return (
    <div className={styles.markdown}>
      {/* MarkdownTextPrimitive with smart buffering preprocess */}
      <MarkdownTextPrimitive
        remarkPlugins={[remarkGfm]}
        className={styles.markdown}
        components={defaultComponents}
        preprocess={preprocess}
      />

      {/* Loading indicator when content is buffered */}
      {hasBufferedContent && sections.length === 0 && (
        <div className={styles.loadingIndicator}>
          <ChatTextAnimation className={styles.loadingText}>
            Loading...
          </ChatTextAnimation>
          <LoadingOutlined spin />
        </div>
      )}

      {/* Render collapsible sections after main content */}
      {sections.map((section, index) => {
        // Generate appropriate display title based on section type
        const getDisplayTitle = () => {
          switch (section.type) {
            case "table":
              const formatSuffix = section.format
                ? ` (${section.format.toUpperCase()})`
                : "";
              return `📊 ${section.title}${formatSuffix}`;
            case "collapsible":
            default:
              return section.title;
          }
        };

        return (
          <CollapsibleSection
            key={`${section.title}-${section.type}-${index}`}
            title={getDisplayTitle()}
            content={section.content}
            isLoading={section.isLoading}
          />
        );
      })}
    </div>
  );
}
